/**
 * Tipos e chamadas do cadastro de cliente.
 *
 * O contrato espelha `api/internal/modulos/clientes/dominio.go`. Dois detalhes
 * carregam significado:
 *
 * `fonte` diz de onde o número saiu. `aguarda_venda` significa que o módulo de
 * venda não existe e o bloco NÃO tem dado — a tela mostra o texto de `espera`
 * em vez de desenhar zero, que pareceria medição.
 *
 * `documento` e `telefone` chegam CRUS. A máscara é da tela (ver
 * plataforma/mascaras.ts), pelo mesmo motivo de dinheiro chegar em centavos: o
 * valor cru atravessa, a formatação é da borda.
 *
 * E não existe `id`. A identidade que atravessa a fronteira é o `codigo`
 * (`C0001`), sequencial por loja — CONSTITUICAO.md §2: "o usuário digita e vê
 * o código, jamais o id". A API não emite o uuid, e há teste de fluxo que
 * falha se algum campo voltar a carregá-lo.
 */
import { api } from './api'
import type { Centavos } from './dinheiro'

export type TipoCliente = 'pf' | 'pj'
export type StatusCliente = 'ativo' | 'inativo'
export type Filtro = 'todos' | 'vip' | 'crediario' | 'inativos'
export type Fonte = 'real' | 'aguarda_venda'
export type FormatoKPI = 'dinheiro' | 'inteiro'

export type KPI = {
  chave: string
  rotulo: string
  formato: FormatoKPI
  valor: number
  fonte: Fonte
  detalhe?: string
  espera?: string
}

export type ContagemFiltro = {
  chave: Filtro
  rotulo: string
  total: number
}

export type Bloco = {
  fonte: Fonte
  espera?: string
}

export type Crediario = {
  liberado: boolean
  limite_centavos: Centavos
  parcelas_max: number
  dia_vencimento?: number
  rotulo: string
  /**
   * O limite CONFIGURADO é real; o USADO depende de venda. É por isso que o
   * saldo tem fonte própria, separada do resto do bloco.
   */
  fonte_saldo: Fonte
  espera_saldo?: string
}

export type LinhaCliente = {
  /** `C0001` — a identidade que o balcão dita ao telefone. */
  codigo: string
  nome: string
  nome_fantasia?: string
  tipo: TipoCliente
  tipo_rotulo: string
  documento?: string
  telefone?: string
  email?: string
  etiquetas: string[]
  tamanho_preferido?: string
  numeracao_preferida?: string
  cores_preferidas: string[]
  crediario: Crediario
  status: StatusCliente
  status_rotulo: string
  criado_em: string
}

export type ListaDeClientes = {
  kpis: KPI[]
  filtros: ContagemFiltro[]
  filtro: Filtro
  busca?: string
  /** Declara, para a tabela inteira, que "última compra" e "total gasto" ainda
   *  não têm de onde sair. */
  historico: Bloco
  linhas: LinhaCliente[]
  total: number
  /** Tamanho da base inteira — é o "N de M" do rodapé. */
  base: number
}

export type Contato = {
  telefone?: string
  email?: string
  /**
   * Os DOIS consentimentos, separados (LGPD). Nenhuma linha do front deriva um
   * do outro: cupom da própria compra e campanha são autorizações diferentes.
   */
  aceita_whatsapp: boolean
  aceita_marketing: boolean
}

export type Endereco = {
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  uf?: string
  /** O servidor já diz se dá para cobrar — o checklist do rail lê daqui. */
  completo: boolean
}

export type Preferencia = {
  tamanho?: string
  numeracao?: string
  cores: string[]
}

export type Compra = {
  quando: string
  resumo: string
  total_centavos: Centavos
}

export type BlocoCompras = Bloco & { linhas: Compra[] }

export type FichaCliente = {
  codigo: string
  nome: string
  nome_fantasia?: string
  tipo: TipoCliente
  tipo_rotulo: string
  documento?: string
  /** "aaaa-mm-dd" — o formato de `<input type="date">`. */
  nascimento?: string
  inscricao_estadual?: string
  contato: Contato
  endereco: Endereco
  preferencia: Preferencia
  crediario: Crediario
  etiquetas: string[]
  observacao_interna?: string
  status: StatusCliente
  status_rotulo: string
  compras: BlocoCompras
  criado_em: string
  atualizado_em: string
}

/**
 * Crediário do pedido. AUSENTE significa "não mexer".
 *
 * Liberar crediário exige `credit.enable` no servidor, e um vendedor precisa
 * conseguir corrigir o telefone de um cliente que já tem crédito. Por isso o
 * front só manda este bloco quando a pessoa realmente mexeu nele — e por isso
 * o campo é opcional, e não um objeto sempre preenchido cujo zero-value
 * revogaria o crédito a cada edição de endereço.
 */
export type PedidoCrediario = {
  liberado: boolean
  limite_centavos: Centavos
  parcelas_max: number
  dia_vencimento?: number
}

export type PedidoCliente = {
  tipo: TipoCliente
  nome: string
  nome_fantasia?: string
  documento?: string
  nascimento?: string
  inscricao_estadual?: string
  email?: string
  telefone?: string
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  uf?: string
  tamanho_preferido?: string
  numeracao_preferida?: string
  cores_preferidas?: string[]
  etiquetas?: string[]
  aceita_whatsapp: boolean
  aceita_marketing: boolean
  crediario?: PedidoCrediario
  observacao_interna?: string
  status?: StatusCliente
}

export function carregarClientes(
  busca: string,
  filtro: Filtro,
  sinal?: AbortSignal,
): Promise<ListaDeClientes> {
  const params = new URLSearchParams()
  if (busca) params.set('busca', busca)
  if (filtro && filtro !== 'todos') params.set('filtro', filtro)
  return api.get<ListaDeClientes>(`/clientes?${params}`, sinal)
}

export function carregarFicha(codigo: string, sinal?: AbortSignal): Promise<FichaCliente> {
  return api.get<FichaCliente>(`/clientes/${encodeURIComponent(codigo)}`, sinal)
}

export function criarCliente(pedido: PedidoCliente): Promise<FichaCliente> {
  return api.post<FichaCliente>('/clientes', pedido)
}

export function salvarCliente(
  codigo: string,
  pedido: PedidoCliente,
): Promise<FichaCliente> {
  return api.put<FichaCliente>(`/clientes/${encodeURIComponent(codigo)}`, pedido)
}

/** Diz se o bloco tem dado de verdade. */
export function temDado(bloco: Bloco): boolean {
  return bloco.fonte === 'real'
}

/**
 * Etiquetas que o protótipo oferece no cadastro.
 *
 * É sugestão, não lista fechada: a coluna é `text[]` livre, porque quem decide
 * o que é cliente VIP é a loja, e o critério muda. O filtro da listagem é que
 * é fixo em "VIP".
 */
export const ETIQUETAS_SUGERIDAS = [
  'VIP',
  'Recorrente',
  'Atacado',
  'E-commerce',
  'Box Alpha',
  'Aniversariante do mês',
]

/** Cor de destaque por etiqueta conhecida — o resto sai em neutro. */
export const ESTILO_ETIQUETA: Record<string, string> = {
  VIP: 'bg-marca-suave text-marca-forte',
  Atacado: 'bg-tinta text-white',
  Novo: 'bg-ok-suave text-ok',
}

/** Tamanhos de roupa e numerações que o protótipo oferece. */
export const TAMANHOS_ROUPA = ['PP', 'P', 'M', 'G', 'GG', 'XG']
export const NUMERACOES = ['34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44']

/** Paleta de cores preferidas do protótipo. */
export const CORES_SUGERIDAS: { nome: string; hex: string }[] = [
  { nome: 'Preto', hex: '#1F2937' },
  { nome: 'Cinza', hex: '#9CA3AF' },
  { nome: 'Azul', hex: '#2563EB' },
  { nome: 'Branco', hex: '#F5F5F5' },
  { nome: 'Verde militar', hex: '#4D5C3A' },
]

/** Unidades federativas, para o seletor de UF. */
export const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT',
  'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
]
